import React, { useState, useEffect } from 'react';
import { FaHome, FaLeaf, FaUser, FaTractor, FaSeedling, FaVideo, FaTint, FaSignOutAlt, FaSatellite } from 'react-icons/fa';
import { GiWaterDrop } from 'react-icons/gi';
import { TbVaccine } from 'react-icons/tb';
import { MdLanguage } from 'react-icons/md';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Landmark } from 'lucide-react';

const Sidebar = ({ isOpen = true, isMobile = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('farmer');

  // Get user role from localStorage and current route
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'farmer');
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUserRole('farmer');
      }
    }

    // Also detect role from current URL path as fallback
    if (location.pathname.startsWith('/state')) {
      setUserRole('state');
    } else if (location.pathname.startsWith('/district')) {
      setUserRole('district');
    } else if (location.pathname.startsWith('/govt')) {
      setUserRole('govt');
    } else if (location.pathname.startsWith('/farmer')) {
      setUserRole('farmer');
    }
  }, [location.pathname]);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('loggedIn');
    navigate('/');
  };

  // Define navigation items based on user role
  const getNavItems = () => {
    switch (userRole) {
      case 'govt':
        return [
          { label: 'Dashboard', icon: <FaHome />, path: '/govt' },
          { label: 'Ground Water', icon: <FaTint />, path: '/govt/groundwater' },
          { label: 'Crop Monitoring', icon: <FaSatellite />, path: '/govt/crop-monitoring' },
          { label: 'Crop Advisory', icon: <FaLeaf />, path: '/govt/crop-advisory' },
          { label: 'Water Resources', icon: <GiWaterDrop />, path: '/govt/water-resources' },
          { label: 'Mandi Price Market', icon: <FaUser />, path: '/govt/crops-market' },
          { label: 'Gov Schemes', icon: <Landmark />, path: '/govt/gov-schemes' },
        ];
      case 'state':
        return [
          { label: 'Dashboard', icon: <FaHome />, path: '/state' },
          { label: 'Ground Water', icon: <FaTint />, path: '/state/groundwater' },
          { label: 'Water Resources', icon: <GiWaterDrop />, path: '/state/water-resources' },
          { label: 'Crop Advisory', icon: <FaLeaf />, path: '/state/crop-advisory' },
          { label: 'Mandi Price Market', icon: <FaUser />, path: '/state/crops-market' },
          { label: 'Fertilizer Resources', icon: <FaSeedling />, path: '/state/fertilizer-resources' },
          { label: 'Gov Schemes', icon: <Landmark />, path: '/state/gov-schemes' },
        ];
      case 'district':
        return [
          { label: 'Dashboard', icon: <FaHome />, path: '/district' },
          { label: 'Ground Water', icon: <FaTint />, path: '/district/groundwater' },
          { label: 'Water Resources', icon: <GiWaterDrop />, path: '/district/water-resources' },
          { label: 'Crop Advisory', icon: <FaLeaf />, path: '/district/crop-advisory' },
          { label: 'Mandi Price Market', icon: <FaUser />, path: '/district/crops-market' },
          { label: 'Fertilizer Resources', icon: <FaSeedling />, path: '/district/fertilizer-resources' },
          { label: 'Gov Schemes', icon: <Landmark />, path: '/district/gov-schemes' },
        ];
      default: // farmer
        return [
          { label: 'Dashboard', icon: <FaHome />, path: '/farmer' },
          { label: 'Artificial Harvesting', icon: <MdLanguage />, path: '/farmer/artificial-harvesting' },
          { label: 'Crop Diseases', icon: <TbVaccine />, path: '/farmer/fertilizer' },
          { label: 'Crop Advisory', icon: <FaLeaf />, path: '/farmer/crop-advisory' },
          { label: 'Water Resources', icon: <GiWaterDrop />, path: '/farmer/water-resources' },
          { label: 'Mandi Price Market', icon: <FaUser />, path: '/farmer/crops-market' },
          { label: 'Fertilizer Resources', icon: <FaSeedling />, path: '/farmer/fertilizer-resources' },
          { label: 'Video Resources', icon: <FaVideo />, path: '/farmer/farming-videos' },
          { label: 'Gov Schemes', icon: <Landmark />, path: '/farmer/gov-schemes' },
        ];
    }
  };

  const navItems = getNavItems();

  const sidebarStyle = {
    ...styles.sidebar,
    transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.3s ease',
    zIndex: isMobile ? 100 : 100,
  };

  return (
    <div style={sidebarStyle}>
      {/* Mobile close button */}
      {isMobile && (
        <button
          onClick={onClose}
          aria-label="Close navigation menu"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '22px',
            cursor: 'pointer',
            color: '#007b3c',
            padding: '8px',
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
          }}
        >
          ✕
        </button>
      )}
      <div style={styles.topSection}>
        {/* Logo Section */}
        <div style={styles.header}>
          <a href="https://anuvadini.aicte.gov.in/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
            <img
              src="/anuvadini-logo.png"
              alt="Anuvadini Agro AI Logo"
              style={{
                width: '240px',
                height: 'auto',
                maxHeight: '90px',
                objectFit: 'contain',
                margin: '0',
                display: 'block',
                cursor: 'pointer',
                transition: 'height 0.3s ease, transform 0.3s ease',
                willChange: 'height, transform'
              }}
            />
          </a>
        </div>

        {/* Navigation Items */}
        <div style={styles.navSection}>
          {navItems.map((item) => (
            <Link to={item.path} key={item.label} style={{ textDecoration: 'none' }} onClick={isMobile ? onClose : undefined}>
              <div
                style={{
                  ...styles.navItem,
                  ...(location.pathname === item.path ? styles.active : {}),
                }}
              >
                {item.icon}
                <span>{item.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Logout Button at Bottom */}
      <div style={styles.bottomSection}>
        <div
          onClick={handleLogout}
          style={styles.logoutButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc3545';
            e.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#fff5f5';
            e.currentTarget.style.color = '#dc3545';
          }}
        >
          <FaSignOutAlt />
          <span>Logout</span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  sidebar: {
    width: '280px',
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e0f5e9',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '20px',
    boxSizing: 'border-box',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100%',
    overflowY: 'auto',
    zIndex: 100,
  },
  topSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    paddingTop: '20px',
    borderTop: '1px solid #e0f5e9',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  logoBox: {
    backgroundColor: '#28a745',
    padding: '10px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    color: '#007b3c',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
  active: {
    backgroundColor: '#e8fff0',
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 12px',
    borderRadius: '10px',
    color: '#dc3545',
    backgroundColor: '#fff5f5',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    border: '1px solid #ffcdd2',
    fontSize: '15px',
  },
};

export default Sidebar;